// src/data/agreementDocument.js - task list section 764 (ruling 324 step 3; Bob 2026-10-21 19:42 ET: "make the agreements we
// currently do have complete usable, referenceable documents"). Pure. Builds the rule-by-rule reading of one agreement from:
//   - the rule TEXT in the installation's catalogues (agreementCatalogues.js; Amtrak's in amtrakCatalog.js, with its dated
//     changes, AMTRAK_RULE_MODS, layered under each rule in date order);
//   - where each rule sits in its source file, what the word-for-word comparison of 2026-10-22 found, and every gap, from
//     agreementDocuments.json (written by scripts\gen-agreement-documents.js from the General Chairman's review list).
// A gap is named and never filled from memory; where a source file on disk has its text, that text is shown as the file's
// (section 765). Every reading carries AGREEMENT_UNCONFIRMED_TEXT: no agreement is presented as accurate or
// complete until its General Chairman signs off on it or replaces it. Tested by scripts\test-agreement-documents.mjs.
import DOCS from './agreementDocuments.json';
import { BASE_CATALOGUES, PREAMBLES } from './agreementCatalogues';
import { AMTRAK_BASE_RULES, AMTRAK_RULE_MODS } from './ruleCatalog';
import { AGREEMENT_UNCONFIRMED_TEXT } from './agreementStatus';

const docs = () => (DOCS && Array.isArray(DOCS.documents) ? DOCS.documents : []);

// The agreement document for an agreement id from agreementTables.js, or null when this installation has none for it.
export function documentForAgreement(agreementId) {
  const id = Number(agreementId);
  return docs().find((d) => (d.agreementIds || []).includes(id)) || null;
}

export function agreementDocumentFamilies() { return docs().map((d) => d.family); }

function rulesFor(family) {
  if (family === 'amtrak-ym') return AMTRAK_BASE_RULES || {};
  const c = (BASE_CATALOGUES || []).find((x) => x.family === family);
  return c ? c.rules : null;
}

const byKey = (a, b) => {
  const na = /^\d+$/.test(a) ? Number(a) : Infinity, nb = /^\d+$/.test(b) ? Number(b) : Infinity;
  return na !== nb ? na - nb : String(a).localeCompare(String(b));
};

const CHECKED = {
  MATCHES: 'Matches its source file word for word.',
  DIFFERS: 'Differs from its source file. Read it against your signed agreement.',
};
function checkOf(r, sourceKind) {
  if (!r) return { result: 'NOT COMPARED', label: 'Not yet compared with its source file.', note: '' };
  const label = CHECKED[r.result] || r.result;
  const ocr = sourceKind === 'ocr' && r.result === 'DIFFERS' ? ' The source file is a machine reading of a scan, so the difference may be in the source file.' : '';
  return { result: r.result, label: label + ocr, note: r.result === 'DIFFERS' ? r.note : '' };
}

const ACTION = {
  revised: 'Changed by', revised_in_full: 'Replaced in full by', revised_renumbered: 'Changed and renumbered by',
  deleted: 'Deleted by', not_applicable: 'Does not apply, under', added: 'Added by',
};
function layersFor(family, key, doc) {
  if (family !== 'amtrak-ym') return [];
  const mods = (AMTRAK_RULE_MODS && AMTRAK_RULE_MODS[key]) || [];
  return mods.slice().sort((a, b) => String(a.date).localeCompare(String(b.date))).map((m) => {
    const cmp = (doc.layers || []).find((l) => l.rule === key && l.instrument === m.instrument && l.date === m.date);
    const gap = (doc.gaps || []).find((g) => new RegExp('^' + key + ' \\(' + String(m.instrument).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ',').test(g.ref));
    const needsText = /^revised/.test(m.action);
    return {
      instrument: m.instrument, date: m.date, action: m.action,
      heading: (ACTION[m.action] || m.action) + ' ' + m.instrument + ' (' + m.date + ')',
      where: m.scopeType === 'system_wide' ? 'Everywhere.' : (m.scopeValue ? 'Where: ' + m.scopeValue + '.' : ''),
      notes: m.notes || '',
      text: m.revisedText || '',
      textMissing: needsText && !m.revisedText,
      // Section 765: what the source file has for it, when the platform's claim tools do not carry it yet.
      sourceText: !m.revisedText && gap && gap.sourceText ? gap.sourceText : '',
      sourceFile: gap && gap.sourceFile ? gap.sourceFile : '',
      sourceNote: gap && gap.sourceNote ? gap.sourceNote : '',
      page: cmp ? cmp.page : (gap ? gap.page : ''),
      check: cmp ? checkOf(cmp, doc.sourceKind) : null,
    };
  });
}

const anchorOf = (family, key) => 'agr-' + family + '-' + String(key).toLowerCase().replace(/[^a-z0-9]+/g, '-');

// The whole reading of one agreement document.
export function buildAgreementDocument(doc) {
  if (!doc) return null;
  const rules = doc.fullTextOnPlatform ? rulesFor(doc.family) : null;
  const entries = [];
  const pre = (PREAMBLES && PREAMBLES[doc.family]) || {};
  for (const k of Object.keys(pre)) {
    const p = pre[k];
    const r = (doc.preamble || {})[k];
    entries.push({ key: k, anchor: anchorOf(doc.family, 'pre-' + k), label: p.title || k, title: '', text: p.text || '',
      page: r ? r.page : '', check: checkOf(r, doc.sourceKind), baseText: '', inForce: null, layers: [] });
  }
  if (rules) {
    for (const k of Object.keys(rules).sort(byKey)) {
      const e = rules[k];
      const r = (doc.rules || {})[k];
      const inf = (doc.inForce || {})[k];
      entries.push({
        key: k, anchor: anchorOf(doc.family, k),
        label: k === 'NEW' ? 'New rule' : doc.unit + ' ' + k, title: e.title || '', text: e.text || '',
        page: r ? r.page : '', check: checkOf(r, doc.sourceKind),
        baseText: e.modified && e.baseText ? e.baseText : '',
        inForce: inf ? checkOf(inf, doc.sourceKind) : null,
        layers: layersFor(doc.family, k, doc),
      });
    }
  }
  // Section 766: an agreement with no catalogue reads rule by rule from its source file, said so on every rule.
  const sr = !doc.fullTextOnPlatform && doc.sourceRules ? doc.sourceRules : null;
  if (sr) {
    const ocr = doc.sourceKind === 'ocr' ? ' The file is a machine reading of a scan and may carry its errors.' : '';
    for (const k of Object.keys(sr.rules).sort(byKey)) {
      const e = sr.rules[k];
      entries.push({ key: k, anchor: anchorOf(doc.family, k), label: doc.unit + ' ' + k, title: e.title, text: e.text, page: e.page,
        check: { result: 'SOURCE', label: 'As the source file has it (' + sr.file + ', ' + e.sourceLines + '). Not part of the claim tools yet.' + ocr, note: '' },
        baseText: '', inForce: null,
        // Section 775: the later changes on file, from their source files, under the rule each changes, oldest first.
        layers: (doc.sourceLayers || []).filter((l) => l.rule === k).sort((x, y) => String(x.date).localeCompare(String(y.date))).map((l) => ({
          instrument: l.instrument, date: l.date, action: l.action,
          heading: (ACTION[l.action] || l.action) + ' ' + l.instrument + ' (' + l.date + ')',
          where: '', notes: '', text: '', textMissing: false, page: '', check: null,
          sourceText: l.sourceText, sourceFile: l.sourceFile + ', ' + l.sourceLines, sourceNote: l.sourceNote || '',
        })) });
    }
  }
  const gaps = (doc.gaps || []).map((g) => ({ what: g.what, page: g.page, where: g.where,
    sourceText: g.sourceText || '', sourceFile: g.sourceFile || '', sourceLines: g.sourceLines || '', sourceNote: g.sourceNote || '' }));
  if (!doc.fullTextOnPlatform) {
    gaps.unshift(sr
      ? { what: 'This agreement\'s rules are not in the claim tools yet. Each rule below is shown as its source file has it. Only the ' + doc.quotesCompared
        + ' quotations from it used in discipline cases are in the claim tools, and each was compared with its source file. ' + ((doc.sourceLayers || []).length ? 'Later changes on file are shown under the rules they change, as their source files have them: ' : 'Later changes on file are not layered under the rules they change: ')
        + (doc.sources || []).slice(1).map((s) => s.file).join('; ') + '.', page: '', where: sr.file, sourceText: '', sourceFile: '', sourceLines: '', sourceNote: '' }
      : { what: 'The full text of this agreement is not on the platform yet. Only the ' + doc.quotesCompared
        + ' quotations from it used in discipline cases are, and each was compared with its source file.', page: '', where: (doc.sources || []).map((s) => s.file).join('; '), sourceText: '', sourceFile: '', sourceLines: '', sourceNote: '' });
  }
  return {
    family: doc.family, title: doc.title, unit: doc.unit, unconfirmed: AGREEMENT_UNCONFIRMED_TEXT,
    sourceKind: doc.sourceKind,
    sourceKindText: doc.sourceKind === 'ocr'
      ? 'The source file for this agreement is a machine reading (OCR) of a scanned copy. Page numbers and wording come from it and may carry its errors.'
      : 'The source file for this agreement is text taken from the document itself, not a scan.',
    sources: doc.sources || [], pageNote: doc.pageNote || '', comparedOn: (DOCS && DOCS.comparedOn) || '',
    toc: entries.map((e) => ({ anchor: e.anchor, label: e.label + (e.title ? ' - ' + e.title : '') })),
    entries, gaps, recordGaps: doc.recordGaps || [], reviewNotes: doc.reviewNotes || [],
  };
}

// The reference a member puts in a letter or a claim, e.g. "CSX System Yardmaster Agreement (eff. 2002-07-01), Article 20 -
// Claims or Grievances (printed p.41)".
export function citation(built, entry) {
  if (!built || !entry) return '';
  return built.title + ', ' + entry.label + (entry.title ? ' - ' + entry.title : '') + (entry.page && entry.page !== 'n/d' ? ' (' + entry.page + ')' : '');
}
